import { Head, router, usePage } from "@inertiajs/react";
import type {
	ColumnDef,
	ExpandedState,
	Row,
	Table as TableTanstack,
} from "@tanstack/react-table";
import {
	CheckCircle,
	Ellipsis,
	IdCard,
	Info,
	Loader2,
	Mail,
	Merge,
	SquarePen,
	UserCog,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ChangeEmailDrawer from "@/components/admin-portal/patient/change-email";
import ChangeOwnerDrawer from "@/components/admin-portal/patient/change-owner";
import ToolbarTable from "@/components/admin-portal/patient/data-table-toolbar";
import DetailsContent from "@/components/admin-portal/patient/details-content";
import FormEdit from "@/components/admin-portal/patient/form-edit";
import MergePatientDialog from "@/components/admin-portal/patient/merge-patient";
import PaginationTable from "@/components/admin-portal/shared/data-table-pagination";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { createdAtColumn } from "@/components/shared/created-at-column";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { getGenderIcon } from "@/hooks/use-gender";
import { useIsMobile } from "@/hooks/use-mobile";
import type { GENDERS } from "@/lib/constants";
import { getCsrfToken } from "@/lib/csrf";
import { getRegistrationSourceLabel } from "@/lib/patients";
import {
	cn,
	generateInitials,
	populateQueryParams,
	removeWhiteSpaces,
} from "@/lib/utils";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { Location } from "@/types/admin-portal/location";
import type { Patient } from "@/types/admin-portal/patient";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

interface PageProps {
	patients: {
		data: Patient[];
		metadata: Metadata;
	};
	selectedPatient: null | Patient;
	selectedPatientAppts: {
		metadata: Metadata;
		data: { date: string; schedules: Appointment[] }[];
	} | null;
	filterOptions?: {
		locations: Location[];
	};
	optionsData?: {
		patientGenders: typeof GENDERS;
	};
}
export interface PatientIndexGlobalPageProps
	extends BaseGlobalPageProps,
		PageProps {
	[key: string]: any;
}
export type TableToolbarDataProps = TableTanstack<
	PageProps["patients"]["data"][number]
>;
type TableColumnDef = ColumnDef<PageProps["patients"]["data"][number]>[];
export type TableRowDataProps = Row<PageProps["patients"]["data"][number]>;

export default function Index({
	patients,
	selectedPatient,
	selectedPatientAppts,
}: PageProps) {
	const { t } = useTranslation("patients");
	const isMobile = useIsMobile();
	const { props: globalProps, url: pageURL } =
		usePage<PatientIndexGlobalPageProps>();

	// * for table state
	const currentExpanded = useMemo<ExpandedState>(() => {
		const { queryParams } = populateQueryParams(pageURL);
		const expandedList = queryParams?.expanded
			? removeWhiteSpaces(queryParams?.expanded)?.split(",")
			: [];
		const adminsIndex = patients.data.reduce(
			(obj, item, index) => {
				if (expandedList.includes(String(item.id))) {
					obj[index] = true;
				}
				return obj;
			},
			{} as Record<number, boolean>,
		);

		return adminsIndex;
	}, [pageURL, patients.data]);
	const routeTo = {
		detail: useCallback(
			(id: string, page?: number, limit?: number) => {
				router.get(
					pageURL,
					{ details: id, appt_page: page, appt_limit: limit },
					{
						preserveScroll: true,
						preserveState: true,
						only: [
							"flash",
							"adminPortal",
							"selectedPatient",
							"selectedPatientAppts",
						],
					},
				);
			},
			[pageURL],
		),
		edit: useCallback(
			(id: string) => {
				router.get(
					pageURL,
					{ edit: id },
					{
						preserveScroll: true,
						preserveState: true,
						only: ["flash", "adminPortal", "selectedPatient", "optionsData"],
					},
				);
			},
			[pageURL],
		),
	};
	const dialog = useMemo(() => {
		const currentQuery = globalProps.adminPortal?.currentQuery;
		const isDetailsMode = !!currentQuery?.details;
		const isEditMode = !!currentQuery?.edit;
		const isOpen = isDetailsMode || isEditMode;
		const mode = isDetailsMode ? "details" : isEditMode ? "edit" : "";
		const onOpenChange = (value: boolean) => {
			if (value) return;

			const objQueryParams = { details: null, edit: null };
			const { fullUrl } = populateQueryParams(pageURL, objQueryParams);
			router.get(
				fullUrl,
				{},
				{
					preserveScroll: true,
					preserveState: true,
					only: [
						"flash",
						"adminPortal",
						"selectedPatient",
						"selectedPatientAppts",
						"optionsData",
					],
				},
			);
		};

		return { isOpen, mode, onOpenChange };
	}, [globalProps.adminPortal?.currentQuery, pageURL]);
	const dialogComponent = useMemo<Record<
		typeof dialog.mode,
		JSX.Element
	> | null>(() => {
		if (!selectedPatient) return null;

		return {
			details: (
				<DetailsContent
					open={dialog.isOpen}
					onOpenChange={dialog.onOpenChange}
					patient={selectedPatient}
					appts={selectedPatientAppts}
					onPageChange={(page, limit) =>
						routeTo.detail(selectedPatient.id, page, limit)
					}
				/>
			),
			edit: (
				<FormEdit
					open={dialog.isOpen}
					onOpenChange={dialog.onOpenChange}
					patient={selectedPatient}
				/>
			),
		};
	}, [
		dialog.isOpen,
		dialog.onOpenChange,
		selectedPatient,
		selectedPatientAppts,
		routeTo.detail,
	]);
	const [isVerifying, setIsVerifying] = useState(false);
	const [userIdToVerify, setUserIdToVerify] = useState<string | null>(null);
	const [changeEmailTarget, setChangeEmailTarget] = useState<{
		userId: string;
		name: string;
		currentEmail: string | null | undefined;
	} | null>(null);
	const [changeOwnerTarget, setChangeOwnerTarget] = useState<{
		patientId: string;
		patientName: string;
		patientNumber?: string;
		patientEmail?: string | null;
		hasUserAccount: boolean;
		orderCount?: number;
	} | null>(null);
	const [mergeTarget, setMergeTarget] = useState<{
		keeperId: string;
		keeperName: string;
		keeperNumber?: string;
	} | null>(null);

	const onVerify = useCallback(async (userId?: string) => {
		if (!userId) {
			toast.error("User ID is missing");
			return false;
		}

		setIsVerifying(true);
		try {
			const csrfToken = getCsrfToken();

			const response = await fetch(`/admin-portal/users/${userId}/verify`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": csrfToken,
				},
			});

			const data = await response.json();

			if (response.ok && data.success) {
				toast.success(data.message || "User verified successfully");
				router.reload({ only: ["patients"] });
				return true;
			} else {
				toast.error(data.message || "Failed to verify user account");
				return false;
			}
		} catch (_error) {
			toast.error("An unexpected error occurred during verification");
			return false;
		} finally {
			setIsVerifying(false);
		}
	}, []);

	const columns = useMemo<TableColumnDef>(() => {
		const items: TableColumnDef = [
			{
				accessorKey: "profile",
				enableSorting: false,
				enableHiding: false,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={t("list.profile")} />
				),
				cell: ({ row }) => {
					const name = row.original.name;
					const initials = generateInitials(name);

					return (
						<button
							type="button"
							title={t("button.detail")}
							onClick={() => routeTo.detail(row.original.id)}
							className="flex items-center w-full gap-2 text-sm text-left rounded-md cursor-pointer group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							<Avatar className="border rounded-lg size-8 bg-muted">
								<AvatarImage src="#" alt={name} />
								<AvatarFallback className="text-xs rounded-lg bg-muted">
									{initials}
								</AvatarFallback>
							</Avatar>
							<div className="flex-1 text-sm leading-tight text-left">
								<p className="font-bold uppercase text-pretty group-hover:underline">
									{name}
								</p>
							</div>
						</button>
					);
				},
			},
		];

		if (!isMobile) {
			items.push(
				{
					accessorKey: "contact",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader column={column} title={t("list.contact")} />
					),
					cell: ({ row }) => {
						const contact = row.original.contact;

						return (
							<div className="flex items-center gap-2 text-sm text-left">
								<Avatar className="border rounded-lg size-8 bg-muted">
									<AvatarImage src="#" alt={contact?.contactName} />
									<AvatarFallback className="text-xs rounded-lg bg-muted">
										<IdCard className="size-4 text-muted-foreground" />
									</AvatarFallback>
								</Avatar>
								<div className="flex-1 text-sm leading-tight text-left">
									<p className="font-bold uppercase text-pretty">
										{contact?.contactName || "N/A"}
									</p>
								</div>
							</div>
						);
					},
				},
				{
					accessorKey: "gender",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.gender.label")}
						/>
					),
					cell: ({ row }) => {
						const gender = row.original.gender;

						return (
							<div>
								<Badge variant="accent" className="px-1.5 py-1 rounded-full">
									<span className="flex items-center gap-0.5 text-xs uppercase">
										{getGenderIcon(gender, "size-3.5")}
										{t(`list.gender.${gender.toLowerCase()}`)}
									</span>
								</Badge>
							</div>
						);
					},
				},
				{
					accessorKey: "location",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.location.label")}
						/>
					),
					cell: ({ row }) => {
						const location = row.original.location;

						if (!location) return <span>N/A</span>;

						return (
							<div>
								<p className="text-pretty">
									{[location.city, location.state, location.country].join(", ")}
								</p>
							</div>
						);
					},
				},
				{
					accessorKey: "isVerified",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.verified.label")}
						/>
					),
					cell: ({ row }) => {
						const isVerified = row.original.isVerified;

						return (
							<div>
								<Badge
									variant="accent"
									className={cn(
										"px-1.5 py-1 rounded-full",
										isVerified ? "bg-green-100" : "bg-red-100",
									)}
								>
									<span
										className={cn(
											"flex items-center gap-1 text-[10px] uppercase",
											isVerified ? "text-emerald-500" : "text-red-500",
										)}
									>
										{isVerified
											? t("list.verified.verified")
											: t("list.verified.unverified")}
									</span>
								</Badge>
							</div>
						);
					},
				},
				{
					accessorKey: "registrationSource",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.registration_source")}
						/>
					),
					cell: ({ row }) => {
						const registrationSource = row.original.registrationSource;

						return (
							<div>
								<span className="text-xs italic uppercase text-muted-foreground/75">
									{getRegistrationSourceLabel(registrationSource)}
								</span>
							</div>
						);
					},
				},
				{
					accessorKey: "accountStatus",
					enableSorting: false,
					enableHiding: false,
					header: ({ column }) => (
						<DataTableColumnHeader
							column={column}
							title={t("list.account_status.label")}
						/>
					),
					cell: ({ row }) => {
						const accountStatus = row.original.accountStatus;
						// A patient may only be owned by a customer account. Linked to an admin or a
						// therapist it is an anomaly — and one the order-repair tool cannot see, since
						// such a patient's orders inherit the same owner and look self-consistent. So
						// it has to announce itself, in red, against the green and grey around it.
						const isAnomalous =
							accountStatus === "admin" || accountStatus === "therapist";

						return (
							<div>
								<Badge
									variant="accent"
									className={cn(
										"px-1.5 py-1 rounded-full",
										isAnomalous
											? "bg-red-100"
											: accountStatus === "customer"
												? "bg-green-100"
												: "bg-muted",
									)}
								>
									<span
										className={cn(
											"text-[10px] uppercase whitespace-nowrap",
											isAnomalous
												? "text-red-500"
												: accountStatus === "customer"
													? "text-emerald-500"
													: "text-muted-foreground",
										)}
									>
										{t(`list.account_status.${accountStatus}`)}
									</span>
								</Badge>
							</div>
						);
					},
				},
				createdAtColumn(t("list.registered_at")),
				{
					accessorKey: "actions",
					enableSorting: false,
					enableHiding: false,
					header: "",
					cell: ({ row }) => {
						const isVerified = row.original.isVerified;
						const isUserExist = !!row.original.userId;

						return (
							<div className="flex items-center justify-end">
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant="ghost" size="icon">
											<Ellipsis />
										</Button>
									</DropdownMenuTrigger>

									<DropdownMenuContent align="end">
										<DropdownMenuLabel>{t("list.actions")}</DropdownMenuLabel>
										<DropdownMenuSeparator />

										<DropdownMenuGroup>
											<DropdownMenuItem
												onSelect={() => routeTo.detail(row.original.id)}
											>
												<Info className="shrink-0 text-muted-foreground/75" />
												{t("button.detail")}
											</DropdownMenuItem>

											<DropdownMenuSeparator />

											<DropdownMenuItem
												onSelect={() => routeTo.edit(row.original.id)}
											>
												<SquarePen className="shrink-0 text-muted-foreground/75" />
												{t("button.edit")}
											</DropdownMenuItem>

											<DropdownMenuItem
												disabled={isVerifying || !isUserExist || isVerified}
												onSelect={() =>
													setUserIdToVerify(row.original.userId || null)
												}
											>
												<CheckCircle className="shrink-0 text-muted-foreground/75" />
												{t("button.verify")}
											</DropdownMenuItem>

											<DropdownMenuItem
												disabled={!isUserExist}
												onSelect={() =>
													setChangeEmailTarget({
														userId: row.original.userId || "",
														name: row.original.name,
														currentEmail: row.original.contact?.email || null,
													})
												}
											>
												<Mail className="shrink-0 text-muted-foreground/75" />
												{t("button.change_email")}
											</DropdownMenuItem>

											<DropdownMenuItem
												onSelect={() =>
													setChangeOwnerTarget({
														patientId: row.original.id,
														patientName: row.original.name,
														patientNumber: row.original.patientNumber,
														patientEmail: row.original.contact?.email ?? null,
														hasUserAccount: !!row.original.hasUserAccount,
														orderCount: row.original.orderCount ?? 0,
													})
												}
											>
												<UserCog className="shrink-0 text-muted-foreground/75" />
												{t("button.change_owner")}
											</DropdownMenuItem>

											<DropdownMenuItem
												onSelect={() =>
													setMergeTarget({
														keeperId: row.original.id,
														keeperName: row.original.name,
														keeperNumber: row.original.patientNumber,
													})
												}
											>
												<Merge className="shrink-0 text-muted-foreground/75" />
												{t("button.merge")}
											</DropdownMenuItem>
										</DropdownMenuGroup>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						);
					},
				},
			);
		}

		return items;
	}, [isMobile, t, routeTo.detail, routeTo.edit, isVerifying]);

	return (
		<>
			<Head title={t("head_title")} />

			<PageContainer className="min-h-[100vh] grid grid-cols-1 md:min-h-min space-y-4">
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{t("page_title")}
						</h1>

						<p className="w-full text-sm md:w-8/12 text-pretty text-muted-foreground">
							{t("page_description")}
						</p>
					</div>
				</div>

				<Separator className="bg-border" />

				<DataTable
					columns={columns}
					data={patients.data}
					toolbar={(table) => <ToolbarTable table={table} />}
					customPagination={(table) => (
						<PaginationTable table={table} metadata={patients.metadata} />
					)}
					currentExpanded={currentExpanded}
				/>

				{dialogComponent?.[dialog.mode]}

				{changeEmailTarget && (
					<ChangeEmailDrawer
						open={!!changeEmailTarget}
						onOpenChange={(open) => !open && setChangeEmailTarget(null)}
						userId={changeEmailTarget.userId}
						patientName={changeEmailTarget.name}
						currentEmail={changeEmailTarget.currentEmail}
					/>
				)}

				{changeOwnerTarget && (
					<ChangeOwnerDrawer
						open={!!changeOwnerTarget}
						onOpenChange={(open) => !open && setChangeOwnerTarget(null)}
						patientId={changeOwnerTarget.patientId}
						patientName={changeOwnerTarget.patientName}
						patientNumber={changeOwnerTarget.patientNumber}
						patientEmail={changeOwnerTarget.patientEmail}
						hasUserAccount={changeOwnerTarget.hasUserAccount}
						orderCount={changeOwnerTarget.orderCount}
					/>
				)}

				{mergeTarget && (
					<MergePatientDialog
						open={!!mergeTarget}
						onOpenChange={(open) => !open && setMergeTarget(null)}
						keeperId={mergeTarget.keeperId}
						keeperName={mergeTarget.keeperName}
						keeperNumber={mergeTarget.keeperNumber}
					/>
				)}

				<AlertDialog
					open={!!userIdToVerify}
					onOpenChange={(open) => !open && setUserIdToVerify(null)}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>{t("confirm.verify_title")}</AlertDialogTitle>
							<AlertDialogDescription>
								{t("confirm.verify")}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={isVerifying}>
								{t("button.cancel")}
							</AlertDialogCancel>
							<Button
								disabled={isVerifying}
								onClick={async () => {
									if (userIdToVerify) {
										const success = await onVerify(userIdToVerify);
										if (success) {
											setUserIdToVerify(null);
										}
									}
								}}
							>
								{isVerifying && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								{t("button.verify")}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</PageContainer>
		</>
	);
}
